import React, { useEffect, useId, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { QRCodeSVG } from "qrcode.react";
import CodeInput from "../../Login/mfa/CodeInput";
import { clock } from "../../Login/mfa/clock";
import "./security.css";
import "./enrollSteps.css";

const useCountdown = (enrollment) => {
  const [remaining, setRemaining] = useState(enrollment?.expires_in ?? 0);

  useEffect(() => {
    setRemaining(enrollment?.expires_in ?? 0);
    const timer = setInterval(() => setRemaining((value) => Math.max(0, value - 1)), 1000);
    return () => clearInterval(timer);
  }, [enrollment]);

  return remaining;
};

// `stepLabel` (optional): "Step 2 of 2" during login setup (docs/specs/login-setup.md §2); absent = today.
const EnrollAuthenticatorModal = ({ enrollment, busy, error, onConfirm, onCancel, onRestart, stepLabel }) => {
  const [code, setCode] = useState("");
  const [showKey, setShowKey] = useState(false);
  const remaining = useCountdown(enrollment);
  const titleId = useId();
  const timedOut = remaining <= 0;

  useEffect(() => {
    setCode("");
    setShowKey(false);
  }, [enrollment]);

  const confirm = () => {
    if (/^[0-9]{6}$/.test(code)) onConfirm(code);
  };

  return (
    <Modal show animation={false} backdrop="static" onHide={onCancel} aria-labelledby={titleId}
      className="mfa-modal" dialogClassName="mfa-enroll-dialog">
      <Modal.Header>
        {stepLabel && <span className="mfa-modal-sub">{stepLabel}</span>}
        <Modal.Title id={titleId}>Set up Authenticator App</Modal.Title>
        {!timedOut && <span className="mfa-modal-sub">3 quick steps · Expires in {clock(remaining)}</span>}
      </Modal.Header>
      <Modal.Body>
        {timedOut ? (
          <>
            <p>Setup timed out.</p>
            {error && <div className="alert alert-danger" role="alert">{error}</div>}
            <div><Button type="button" onClick={onRestart}>Start again</Button></div>
          </>
        ) : (
          <>
            {/* docs/specs/mfa-ux-cleanup.md §5 (Row 3, option A): three numbered steps on one screen. */}
            <ol className="mfa-enroll-steps">
              <li>
                <strong>Install an authenticator app on your phone</strong>
                <p>
                  Google Authenticator or Microsoft Authenticator, free from the App Store or Google Play. Already have
                  one? Go to step 2.
                </p>
              </li>
              <li>
                <strong>Scan this QR code with the app</strong>
                {/* Phones only (security.css): the otpauth link opens the user's authenticator app on this phone. It is
                    rendered from memory like the QR, never logged or stored. Amar, 2026-10-22. */}
                <div className="mfa-open-app">
                  <a className="btn btn-primary text-white fw-bold" href={enrollment.otpauth_uri}>Open in authenticator app</a>
                  <p>Using another device? Scan the QR code below, or take a screenshot and open it in your app.</p>
                </div>
                <div className="mfa-enroll-scan">
                  <QRCodeSVG value={enrollment.otpauth_uri} size={160} level="M" marginSize={2} className="mfa-qr" />
                  <div>
                    <p>In the app, tap + (Add account), then scan.</p>
                    <div>
                      <Button variant="link" type="button" className="p-0" onClick={() => setShowKey(true)}>
                        Can&apos;t scan? Show the key to type in
                      </Button>
                    </div>
                    {showKey && <code className="mfa-key">{enrollment.secret_base32}</code>}
                  </div>
                </div>
              </li>
              <li>
                {/* The boxes' own label names this step ("Enter the 6-digit code from your authenticator app"). */}
                <CodeInput method="totp" value={code} onChange={setCode} disabled={busy} />
              </li>
            </ol>
            {error && <div className="alert alert-danger mb-0" role="alert">{error}</div>}
          </>
        )}
      </Modal.Body>
      {!timedOut && (
        <Modal.Footer>
          <Button variant="outline-secondary" className="mfa-btn-quiet" type="button" disabled={busy} onClick={onCancel}>Cancel</Button>
          <Button type="button" className="text-white fw-bold" disabled={busy} onClick={confirm}>Confirm and activate</Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default EnrollAuthenticatorModal;
